import { FC, useState, useEffect, Suspense, ReactElement, FunctionComponent } from 'react';
import { ChakraProvider, Flex } from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Dict } from '@chakra-ui/utils';
import loadable, { LoadableComponent } from '@loadable/component';
import UserBootstrapper from 'components/UserBootstrapper';
import { Route, Switch, Redirect, useHistory, useLocation, RouteChildrenProps } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorBoundary } from '@sentry/react';
import qs from 'qs';
import {
  appContextSelector,
  logoSelector,
  featuresSelector,
  themeNameSelector,
  setTheme,
  setLogo,
  isCorporateThemeSelector,
  loadAdditionalTheme,
  unloadAdditionalTheme,
  externalThemeLoadedSelector,
} from 'modules/app/appSlice';
import { FullPageLoader } from 'components/Loader/Loader';
import { ErrorFallback, MainBox } from 'components';
import { RootState } from 'store';
import GlobalStyles from 'styles/Global';
import { theme as defaultTheme, GlobalThemeStyles as DefaultGlobalThemeStyles } from './themes/default';
import { FeaturesProvider } from 'utils/featuresContext';
import Invite from 'pages/shared/Invite/Invite';
import './i18n';
import Project from 'pages/shared/Project';
import Toaster from 'components/Toaster/Toaster';
import LoginSetup from 'pages/shared/LoginSetup/LoginSetup';
import { getPartnerSettings } from 'utils/partnerSettings';
import GlobalTerms, { TermsTheme } from 'pages/wechooose/GlobalTerms';
import { currentUserSelector, updateLanguage } from 'modules/user/userSlice';
import { useCookies } from 'react-cookie';

// Apps
const WechoooseApp = loadable(() => import(/* webpackPrefetch: true */ './subapps/wechooose/SubApp'));
const WeChoooseGuestApp = loadable(() => import(/* webpackPrefetch: true */ './subapps/wechooose/GuestSubApp'));
const MyChoooseGuestApp = loadable(() => import(/* webpackPrefetch: true */ './subapps/mychooose/GuestSubApp'));
const MychoooseApp = loadable(() => import(/* webpackPrefetch: true */ './subapps/mychooose/SubApp'));
const ConnectApp = loadable(() => import(/* webpackPrefetch: true */ './subapps/connect/SubApp'));

// Pages
const EnterpriseLogin = loadable(() => import(/* webpackPrefetch: true */ './pages/shared/EnterpriseLogin/EnterpriseLogin'));
const SignupTypePicker = loadable(() => import(/* webpackPrefetch: true */ './pages/shared/SignupTypePicker/SignupTypePicker'));
const WeChoooseSignup = loadable(() => import(/* webpackPrefetch: true */ './pages/wechooose/Signup'));
const WeChoooseSetup = loadable(() => import(/* webpackPrefetch: true */ './pages/wechooose/Setup'));
const MyChoooseSignup = loadable(() => import(/* webpackPrefetch: true */ './pages/mychooose/Signup'));
const MyChoooseSetup = loadable(() => import(/* webpackPrefetch: true */ './pages/mychooose/Setup'));
const MyChoooseGuestSigninSignup = loadable(() => import(/* webpackPrefetch: true */ './pages/mychooose/GuestSigninSignup'));
const GiftClaim = loadable(() => import(/* webpackPrefetch: true */ './pages/mychooose/GiftClaim'));
const OrderClaim = loadable(() => import(/* webpackPrefetch: true */ './pages/wechooose/OrderClaim/OrderClaimGuest'));

const App: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [cookies, setCookie] = useCookies(['chooose-cookies-consent-state']);
  const [themeLoading, setThemeLoading] = useState(false);
  const [appTheme, setAppTheme] = useState<{ theme: Dict; GlobalThemeStyles: FunctionComponent }>({
    theme: defaultTheme,
    GlobalThemeStyles: DefaultGlobalThemeStyles,
  });
  const context = useSelector(appContextSelector);
  const logo = useSelector(logoSelector);
  const features = useSelector(featuresSelector);
  const appThemeName = useSelector(themeNameSelector);
  const isExternalThemeLoaded = useSelector(externalThemeLoadedSelector);
  const currentLanguage = i18n.language;

  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const languages = useSelector((state: RootState) => state.app.languages);
  const currentAccount = useSelector(currentUserSelector);
  let SubApp: LoadableComponent<{}>;
  let GuestSupApp: LoadableComponent<RouteChildrenProps<{}, unknown>>;
  const shouldLoadExternalTheme = (context !== 'guest-mychooose' && context !== 'mychooose') || location.pathname.startsWith('/project/');
  const isGuestCertificate = location.pathname.startsWith('/guest/certificate');

  const params: { theme?: string; inviteId?: string; claimCode?: string; partnershipId?: string } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  if (context === 'guest-wechooose') {
    GuestSupApp = WeChoooseGuestApp;
  } else {
    GuestSupApp = MyChoooseGuestApp;
  }

  if (context === 'wechooose') {
    SubApp = WechoooseApp;
  } else if (context === 'connect') {
    SubApp = ConnectApp;
  } else {
    SubApp = MychoooseApp;
  }

  useEffect(() => {
    async function loadTheme(themeName: string) {
      if (!themeName || themeName === appThemeName) {
        return;
      }

      if (themeName === 'default') {
        setAppTheme({ theme: defaultTheme, GlobalThemeStyles: DefaultGlobalThemeStyles });
        dispatch(setTheme('default'));
        return;
      }

      setThemeLoading(true);
      try {
        const newTheme = await import(`./themes/${themeName}`);
        setAppTheme({ theme: newTheme.theme as Dict, GlobalThemeStyles: newTheme.GlobalThemeStyles });
        dispatch(setTheme(themeName));
      } catch (err) {
        captureException(err);
      } finally {
        setThemeLoading(false);
      }
    }

    const termsToPortalTheme = {
      chooosepersonal: 'default',
      chooosecompany: 'default',
      partnerpersonal: 'corporate',
      partnercompany: 'corporate',
    };
    let theme = location.pathname.includes('/terms') ? termsToPortalTheme[(params.theme as TermsTheme) ?? 'chooosepersonal'] : undefined;
    if (isGuestCertificate && params.partnershipId) {
      theme = 'corporate';
    }

    let themeToLoad = 'default';

    if (shouldLoadExternalTheme) {
      themeToLoad = process.env.REACT_APP_THEME || theme || 'default';
    }

    const isTermsPageWithAdditionalTheme = params.partnershipId && location.pathname.startsWith('/terms');

    if (!isTermsPageWithAdditionalTheme || isExternalThemeLoaded) {
      loadTheme(themeToLoad);
    }
  }, [context, isExternalThemeLoaded]);

  useEffect(() => {
    if ((context === 'wechooose' || isGuestCertificate) && isCorporateTheme && !logo) {
      const settings = getPartnerSettings();
      dispatch(setLogo(settings.logoUrl));
    }
  }, [context, isCorporateTheme, isGuestCertificate]);

  useEffect(() => {
    // Fallback to english if user's language is not supported
    if (languages && !languages.includes(currentLanguage)) {
      i18n.changeLanguage('en');
    }
  }, [currentLanguage, languages]);

  useEffect(() => {
    if (currentAccount?.profile) {
      const userLanguage = currentAccount?.profile?.language;
      if (currentLanguage !== userLanguage) {
        dispatch(updateLanguage(currentLanguage));
      }
    }
  }, [currentAccount?.customer]);

  useEffect(() => {
    if (isGuestCertificate && params.inviteId && params.claimCode && params.partnershipId) {
      dispatch(loadAdditionalTheme(params.partnershipId));
    }

    return () => {
      dispatch(unloadAdditionalTheme());
    };
  }, [params.partnershipId, isGuestCertificate]);

  useEffect(() => {
    // @ts-ignore
    window.zE && window.zE(() => window.zE.setLocale(currentLanguage));
  }, [currentLanguage]);

  useEffect(() => {
    if (!cookies['chooose-cookies-consent-state']) {
      setCookie('chooose-cookies-consent-state', JSON.stringify({ tracking: true, targeting: true }), { path: '/', maxAge: 60 });
    }
  }, []);

  return (
    <>
      <GlobalStyles />
      <ChakraProvider theme={appTheme.theme} portalZIndex={999}>
        <appTheme.GlobalThemeStyles />
        <ErrorBoundary
          fallback={props => <ErrorFallback {...props} />}
          onReset={(): void => history.replace('/')}
          showDialog={process.env.NODE_ENV !== 'development'}
        >
          <Suspense fallback={<FullPageLoader />}>
            <FeaturesProvider features={features}>
              <Toaster />
              <Switch>
                <Route
                  exact
                  path="/login/enterprise"
                  render={(): ReactElement => (
                    <>
                      {!isAuthenticated ? (
                        <Flex justifyContent="center">
                          <EnterpriseLogin />
                        </Flex>
                      ) : (
                        <Redirect to="/" />
                      )}
                    </>
                  )}
                />
                <Route exact path="/signup" component={SignupTypePicker} />
                <Route exact path="/signup/mychooose" component={MyChoooseSignup} />
                <Route exact path="/setup/mychooose" component={MyChoooseSetup} />
                <Route exact path="/guest-signin-signup/mychooose" component={MyChoooseGuestSigninSignup} />
                <Route exact path="/signup/wechooose" component={WeChoooseSignup} />
                <Route exact path="/setup/wechooose" component={WeChoooseSetup} />
                <Route exact path="/invite" component={Invite} />
                <Route exact path="/login-setup" component={LoginSetup} />
                <Route exact path="/gifted" component={GiftClaim} />
                <Route exact path="/offset-claim" component={OrderClaim} />
                <Route exact path="/terms" component={GlobalTerms} />
                <Route
                  path="/project/:id"
                  render={(): ReactElement => (
                    <MainBox>
                      <Project />
                    </MainBox>
                  )}
                />
                <Route path="/guest" component={GuestSupApp} />
                <Route>
                  <UserBootstrapper>
                    <SubApp />
                  </UserBootstrapper>
                </Route>
              </Switch>
            </FeaturesProvider>
          </Suspense>
          {themeLoading && <FullPageLoader fixed />}
        </ErrorBoundary>
      </ChakraProvider>
    </>
  );
};

export default App;
